import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import CommonStyles from "@components/CommonStyles";
import Payment from "@components/Payment";
import { BankCode } from "@base/enums";
import useCommonSettings from "@base/hooks/useCommonSettings";
import useToggle from "@base/hooks/useToggle";
import { baseRoutes } from "@base/routes/baseRoutes";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import appointmentService from "@base/modules/appointment/appointmentService";
import { formatVnd } from "@base/modules/appointment/bookingCommon";
import { describeBookingError } from "@base/modules/appointment/bookingErrors";
import {
  BOOKING_DRAFT_KEY,
  BookingDraft,
  buildAppointmentOrder,
  buildAppointmentPayload,
  formatBookingDate,
} from "@base/modules/appointment/regularBookingRules";
import orderService from "@base/modules/order/orderService";
import siteService from "@base/modules/site/siteService";

interface IProps {
  draft: BookingDraft;
  onBack: () => void;
  onDone: (path: string) => void;
}

/**
 * The hand-over of a paid booking (§12.4): the appointment is only created here, once a payment
 * method is chosen, then its order - with the BHYT card still on it.
 */
export default function PaymentStep({ draft, onBack, onDone }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { hotline } = useCommonSettings();
  const { open: openTransfer, shouldRender: shouldRenderTransfer, toggle: toggleTransfer } = useToggle();
  const [paymentMethods, setPaymentMethods] = useState<string | undefined>(draft.site?.supported_payment_methods);
  const [bankCode, setBankCode] = useState<string>();
  const [isPaying, setIsPaying] = useState<boolean>(false);
  const [order, setOrder] = useState<{ ref_key?: string; description?: string; payment_url?: string }>();

  //! function
  // The methods are the booked clinic's own.
  useEffect(() => {
    if (!draft.site?.code) {
      return;
    }

    siteService
      .getByCode(draft.site.code)
      .then((resp) => setPaymentMethods(resp.data?.supported_payment_methods))
      .catch(() => undefined);
  }, [draft.site?.code]);

  const alertError = (description: string) => dispatch(setAlertMessage({ type: "error", message: t("Notification"), description }));

  const pay = async () => {
    if (!bankCode) {
      alertError(t("booking.payment.chooseMethod"));
      return;
    }

    let appointment: { key?: string; site_code?: string; site_name?: string };

    try {
      setIsPaying(true);
      const resp = await appointmentService.createAppointment(buildAppointmentPayload(draft, { includeInsurance: true }));

      appointment = resp.data ?? {};
    } catch (error) {
      setIsPaying(false);
      const response = (error as AxiosError).response;
      const result = response ? describeBookingError(response.data, { translate: t, hotline }) : undefined;

      alertError(result?.kind === "message" ? result.text : response ? t("booking.genericError") : t("booking.noConnection"));
      return;
    }

    try {
      const resp = await orderService.createOrder(
        buildAppointmentOrder(draft, appointment, { bankCode, totalAmount: Number(draft.fee?.actual_fee ?? 0) }),
        "vi",
      );
      const created = resp.data ?? {};

      setOrder(created);

      if (bankCode === BankCode.BANK_TRANSFER) {
        toggleTransfer();
        return;
      }

      if (bankCode !== BankCode.CASH && created.payment_url) {
        sessionStorage.removeItem(BOOKING_DRAFT_KEY);
        window.location.replace(created.payment_url);
        return;
      }

      onDone(baseRoutes.userAppointmentDetail.replace(":key", String(appointment.key ?? "")));
    } catch (error) {
      // The appointment exists and can still be paid from its own page.
      alertError(t("booking.payment.orderFailed"));
      onDone(appointment.key ? baseRoutes.userAppointmentDetail.replace(":key", appointment.key) : baseRoutes.userAppointment);
    } finally {
      setIsPaying(false);
    }
  };

  //! render
  const fee = draft.fee ?? {};
  const rows: [string, string | undefined][] = [
    [t("Appointment.service"), draft.service?.name],
    [t("booking.doctorTitle"), draft.doctor?.doctor_name],
    [
      t("booking.preferredTime"),
      draft.slot?.period_start_time
        ? `${dayjs(draft.slot.period_start_time).format("HH:mm")} ${formatBookingDate(draft.date, i18n.resolvedLanguage)}`
        : undefined,
    ],
    [t("Appointment.facility"), [draft.site?.name, draft.branch?.name].filter(Boolean).join(" - ")],
    [t("booking.record.title"), draft.record?.patient_name],
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2 bg-common-background rounded-lg p-4">
        {rows
          .filter(([, value]) => !!value)
          .map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 text-sm font-['Inter']">
              <span className="text-common-subtext shrink-0">{label}</span>
              <span className="text-common-maintext text-right font-medium">{value}</span>
            </div>
          ))}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between text-sm font-['Inter']">
          <span className="text-common-subtext">{t("booking.payment.subtotal")}</span>
          <span className="text-common-maintext">{formatVnd(fee.total_fee)}</span>
        </div>
        <div className="flex justify-between text-sm font-['Inter']">
          <span className="text-common-subtext">{t("booking.payment.discount")}</span>
          <span className="text-common-maintext">{formatVnd(fee.discount)}</span>
        </div>
        <div className="flex justify-between text-base font-semibold font-['Inter']">
          <span className="text-common-maintext">{t("booking.total")}</span>
          <span className="text-common-primary">{formatVnd(fee.actual_fee)}</span>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <span className="text-common-maintext text-base font-semibold font-['Inter']">{t("booking.payment.method")}</span>
        <Payment setBankCode={setBankCode} listPayment={paymentMethods} />
      </div>

      <div className="flex justify-end gap-3">
        <CommonStyles.ButtonUI
          className="h-12 bg-common-primary100 text-common-primary hover:!bg-common-primary200 hover:!text-common-primary"
          disabled={isPaying}
          onClick={onBack}
        >
          {t("booking.back")}
        </CommonStyles.ButtonUI>
        <CommonStyles.ButtonUI className="h-12 px-8" loading={isPaying} onClick={pay}>
          {t("booking.payment.pay")}
        </CommonStyles.ButtonUI>
      </div>

      {shouldRenderTransfer && order ? (
        <CommonStyles.TransferInformationDialog
          resultOrder={order}
          transferContent={order.description}
          open={openTransfer}
          toggle={() => {
            toggleTransfer();
            onDone(order.ref_key ? baseRoutes.userAppointmentDetail.replace(":key", order.ref_key) : baseRoutes.userAppointment);
          }}
        />
      ) : null}
    </div>
  );
}
